import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { languages } from '@shared/helpers/languages.helper';
import { ICandidate } from '@shared/interfaces/candidate.interface';
import { ICountry } from '@shared/interfaces/country.interface';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IPayload } from '@shared/interfaces/payload.interface';
import { IQuestionSet } from '@shared/interfaces/question-set.interface';
import { IRecruiter } from '@shared/interfaces/recruiter.interface';
import { IReferenceRelationship } from '@shared/interfaces/reference-relationship.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { Reference } from '@shared/models/reference.model';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { HiringFirmsService } from '@shared/services/hiring-firms.service';
import { LoggerService } from '@shared/services/logger.service';
import { QuestionSetsService } from '@shared/services/question-sets.service';
import { RecruitersService } from '@shared/services/recruiters.service';
import { ReferencesService } from '@shared/services/references.service';
import { ToastService } from '@shared/services/toast.service';
import { EmailValidityValidator } from '@shared/validators/emailValidity.validators';
import { NumbersOnlyValidator } from '@shared/validators/numbersOnly.validators';
import { PhoneEmailPersonalValidator } from '@shared/validators/phoneEmailPersonal.validators';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';

import { LocalStorage, SessionStorage } from 'ngx-webstorage';

@Component({
    selector: 'app-modals-references',
    templateUrl: './modals.references.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalsReferencesComponent
  extends ModalDirective<IReference>
  implements OnInit
{
  @Input() public isQSEditable = Boolean(true);

  @LocalStorage() public isSubAccount: boolean;

  @SessionStorage() public settings: ISetting;

  @SessionStorage() public countries: ICountry[];
  @SessionStorage() private hiringFirm: IHiringFirm;

  public candidate: Partial<ICandidate>;
  public reference: IReference;

  public title = String('MODALS.REFERENCES.ADD.TITLE');
  public description = String('MODALS.REFERENCES.DESCRIPTION');

  public submitKey = String('BUTTONS.SUBMIT');
  public entry: FormGroup;
  public questionSets: IQuestionSet[] = [];
  public isButtonsDisabled = Boolean(false);
  public isReferenceRequestSent = Boolean(false);
  public isReferenceEmailRequired = Boolean(true);
  public params: any = {
    type: 'reference'
  };

  public relationships: IReferenceRelationship[] = [];
  public languages: any[] = [];
  public recruiters: IRecruiter[] = [];
  public countriesOptions: any[] = [];
  public language = String('en');

  public bsConfig: any = {
    containerClass: 'v-datepicker v-datepicker-range',
    dateInputFormat: 'MMM YYYY',
    rangeInputFormat: 'MMM YYYY',
    maxDate: new Date(),
    showWeekNumbers: false,
    adaptivePosition: true,
    minMode: 'month'
  };

  public isMultiLanguagesEnable = Boolean(false);

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _fb: FormBuilder,
    private readonly _hiringFirms: HiringFirmsService,
    private readonly _recruiters: RecruitersService,
    private readonly _references: ReferencesService,
    private readonly _questionSets: QuestionSetsService,
    private readonly _toast: ToastService,
    private readonly _logger: LoggerService,

    private readonly _emailValidityValidator: EmailValidityValidator
  ) {
    super();
  }

  public get isEmailRequired(): boolean {
    if (!this.isReferenceEmailRequired) {
      return false;
    }

    const control = this.entry.get('email') as FormGroup;
    const validator =
      control?.validator && control?.validator({} as AbstractControl);
    return validator?.required;
  }

  public get isPhoneRequired(): boolean {
    const control = this.entry.get('phone') as FormGroup;
    const validator =
      control?.validator && control?.validator({} as AbstractControl);
    return validator?.required;
  }

  public get isWorkPhoneRequired(): boolean {
    const control = this.entry.get('work_phone') as FormGroup;
    const validator =
      control?.validator && control?.validator({} as AbstractControl);
    return validator?.required;
  }

  ngOnInit(): void {
    this.createForm();
    this.initializeCountriesOptions();
    this.subscribeToRelationshipChanges();

    this.openModal.subscribe(
      (res: { candidate: ICandidate; reference: IReference }) => {
        this.candidate = res.candidate;
        this.reference = res.reference;

        this.isMultiLanguagesEnable =
          this.settings?.is_plugins_multi_languages_enabled;
        this.isReferenceEmailRequired =
          this.settings?.is_reference_email_required;

        this.setDataForm();

        this.languages = languages.filter(
          (l: any) => this.settings.languages.indexOf(l.id) > -1
        );

        if (this.languages.length > 0) {
          this.entry.patchValue({ language: this.languages[0].id });
        }

        if (!!this.reference) {
          this.title = 'MODALS.REFERENCES.EDIT.TITLE';
          this.submitKey = 'BUTTONS.SAVE';

          const endDateControl = this.entry.get('end_date') as FormGroup;
          if (this.settings.is_reference_dates_enabled) {
            if (!!!this.reference.end_date) {
              endDateControl.disable();
              this.entry.patchValue({ isCurrent: true });
            } else {
              endDateControl.enable();
              this.entry.patchValue({ isCurrent: false });
            }

            endDateControl.updateValueAndValidity();
          }

          this.setDataForm();

          this.entry.patchValue({
            request_by: this.reference.requested_by_id,
            question_set: this.reference.question_set?.id,
            start_date: this.reference.start_date
              ? moment(this.reference?.start_date).startOf('month').toDate()
              : null,
            end_date: this.reference?.end_date
              ? moment(this.reference?.end_date).startOf('month').toDate()
              : null
          });
        } else {
          this.isReferenceRequestSent =
            this.candidate.reference_requests_status.not_completed > 0;

          const token = this._commonEnvironments.getToken();
          const payload: IPayload = jwtDecode(token);

          this.entry.patchValue({
            request_by: payload.recruiter_id
          });
        }

        this.getQuestionSets();
        this.getReferenceRelationships();
        this.getRecruiters();
      }
    );
  }

  public getQuestionSets() {
    const url = 'GET /question_sets';
    const filters = {
      page_size: 50,
      filter: {
        key: 'qs',
        value: 'scope:reference'
      }
    };

    this._questionSets.get(filters).subscribe(
      (res: IQuestionSet[]) => {
        this._logger.info(this.constructorName, url, res);

        this.questionSets = res;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.errors = err;
      }
    );
  }

  public onDismiss() {
    this.errors = [];
    this.entry.enable();

    this.resetAll();
  }

  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      this.isButtonsDisabled = true;
      this.entry.disable();
      this.errors = [];

      value = new Reference({
        ...value,
        request_by: this.recruiters.find(
          (r: IRecruiter) => r.id === value.request_by
        ),
        question_set: this.questionSets.find(
          (qs: IQuestionSet) => qs.id === value.question_set
        ),
        start_date: value.start_date
          ? moment(value.start_date).startOf('month').format('YYYY-MM-DD')
          : value.start_date,
        end_date: value.end_date
          ? moment(value.end_date).startOf('month').format('YYYY-MM-DD')
          : null
      });

      if (this.candidate) {
        value.candidate = this.candidate;
      } else if (this.reference && this.reference.candidate) {
        value.candidate = this.reference.candidate;
      }

      if (this.reference && this.reference.profile) {
        value.profile = this.reference.profile;
      }

      if (!!value.id) {
        this.patch(value);
      } else {
        this.post(value);
      }
    }
  }

  public updateCurrentDate() {
    const control = this.entry.controls['end_date'];
    const isCurrent = this.entry.get('isCurrent').value;

    if (!isCurrent) {
      control.enable();
    } else {
      this.entry.patchValue({
        end_date: null
      });

      control.setValidators([]);
      control.disable();
    }

    control.updateValueAndValidity();
  }

  public getLabelForLanguage(
    relationship: IReferenceRelationship,
    languageCode: string
  ): string {
    if (!relationship || !relationship.relationship_languages) {
      return relationship?.label || '';
    }

    const langRecord = relationship.relationship_languages.find(
      (rl: any) => rl.code === languageCode
    );

    return (
      langRecord?.label || relationship.relationship_languages[0]?.label || ''
    );
  }

  public updateWorkPhoneCountryCode() {
    const countryCode = this.entry.value.work_phone_countryInitial;

    let country = this.countries.find(
      (c: ICountry) => c.initial === countryCode
    );

    if (!country) {
      country = this.countries.find((c: ICountry) => c.initial === 'US');
    }

    return this.entry.patchValue({
      work_phone_country_code: country.phoneCode
    });
  }

  private initializeCountriesOptions(): void {
    this.countriesOptions = this.countries?.map((c: ICountry) => ({
      value: c?.initial,
      label: `(+${c?.phoneCode}) ${c?.label['en']}`
    }));
  }

  protected createForm() {
    this.entry = this._fb.group(
      {
        id: [''],
        language: ['en', [Validators.required]],
        question_set: ['', [Validators.required]],
        email: ['', [Validators.email]],
        first_name: ['', [Validators.required]],
        last_name: ['', [Validators.required]],
        title: ['', [Validators.maxLength(63)]],
        company_name: [''],
        relationship: ['U'],
        phone: ['', [NumbersOnlyValidator.NumbersOnly]],
        work_phone: [''],
        work_phone_countryInitial: [''],
        work_phone_country_code: [''],
        work_phone_extension: [''],
        countryInitial: [''],
        country_code: ['', [Validators.required]],
        notes: [''],
        specialty: [''],
        start_date: [''],
        end_date: [''],
        candidate_position: [''],
        isCurrent: [false, [Validators.required]],
        request_by: ['', [Validators.required]]
      },
      {
        validator: PhoneEmailPersonalValidator.DynamicRequired(
          () => this.settings,
          () => this.isReferenceEmailRequired
        )
      }
    );
  }

  private patch(value: IReference) {
    const url = `PATCH /references/${value.id}`;
    this._references.patch(value).subscribe(
      (res: IReference) => {
        this._logger.info(this.constructorName, url, res);

        this._toast.success('TOASTS.REFERENCE_UPDATED');
        this.resetModal();
        this.resetAll();
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.errors = err;
        this.entry.enable();
        this.isButtonsDisabled = false;
      }
    );
  }

  private post(value: any) {
    const url = 'POST /references';
    this._references.addCandidateReference(value).subscribe(
      (res: IReference) => {
        this._logger.info(this.constructorName, url, res);

        this._toast.success('TOASTS.REFERENCE_ADDED');

        this.resetModal();
        this.resetAll();
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.errors = err;
        this.entry.enable();
        this.isButtonsDisabled = false;
      }
    );
  }

  private resetAll() {
    this.entry.enable();
    this.entry.reset();

    this.reference = null;
    this.isButtonsDisabled = false;
  }

  private setDataForm() {
    // First try to find by reference's country code, then fallback to hiring firm
    let country = this.reference?.country_code
      ? this.countries?.find((c: ICountry) => c.phoneCode === String(this.reference.country_code))
      : null;

    if (!country) {
      country = this.countries?.find((c: ICountry) => c.initial === this.hiringFirm?.country);
    }

    const workPhoneCountryCode = this.reference?.work_phone_country_code?.replace('+', '');

    // First try to find by reference's work phone country code, then fallback to hiring firm
    let workPhoneCountry = this.reference?.work_phone_country_code
      ? this.countries?.find((c: ICountry) =>
          c.phoneCode === String(workPhoneCountryCode) ||
          c.phoneCode === String(this.reference.work_phone_country_code))
      : null;

    if (!workPhoneCountry) {
      workPhoneCountry = this.countries?.find((c: ICountry) => c.initial === this.hiringFirm?.country);
    }

    const qsControl = this.entry.get('question_set');
    qsControl.markAsUntouched();
    qsControl.updateValueAndValidity();

    const isCurrentControl = this.entry.get('isCurrent');
    isCurrentControl.clearValidators();
    isCurrentControl.updateValueAndValidity();

    this.entry.patchValue({
      ...this.reference,
      country_code: country?.phoneCode,
      countryInitial: country?.initial,
      work_phone_country_code: workPhoneCountry?.phoneCode,
      work_phone_countryInitial: workPhoneCountry?.initial,
      relationship: ['U'],
      question_set: ''
    });
  }

  private subscribeToRelationshipChanges(): void {
    this.entry.get('relationship').valueChanges.subscribe((value: string) => {
      if (!value || value === 'U') {
        return;
      }
      const rel = this.relationships.find(
        (r: IReferenceRelationship) => r.value === value
      );
      if (rel?.question_set?.id) {
        this.entry.patchValue(
          { question_set: rel.question_set.id },
          { emitEvent: false }
        );
      }
    });
  }

  private getReferenceRelationships() {
    const url = `GET /hiring_firms/${this.hiringFirm.id}/reference_relationships`;
    this._hiringFirms.getReferenceRelationships(this.hiringFirm.id).subscribe(
      (res: IReferenceRelationship[]) => {
        this._logger.info(this.constructorName, url, res);
        this.relationships = res.filter(
          (r: IReferenceRelationship) => r.is_active
        );
      },
      (err) => {
        this._logger.info(this.constructorName, url, err);
      }
    );
  }

  private getRecruiters() {
    this.recruiters = [];

    const filter: any = {
      page_size: 250,
      filter: {
        key: 'is_active',
        value: true
      }
    };

    const url = String('GET /recruiters');
    this._recruiters.get(filter).subscribe(
      (res: IRecruiter[]) => {
        this._logger.info(this.constructorName, url, res);

        this.recruiters = res;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }
}
